import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Phone, Gift, ChevronDown, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { PHONE_CODE_LIST, getCountryByPhoneCode, getCountryByTimezone, getCountryByCode } from '../config/countries';
import Alert from '../components/common/Alert';
import { referralAPI } from '../services/api';
import KeyboardDismissView from '../components/common/KeyboardDismissView';
import { useFormNavigation } from '../hooks/useFormNavigation';
import { openExternalUrl, isIOS } from '../services/native';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';
import AppleLoginButton from '../components/auth/AppleLoginButton';

// Blocked disposable/temporary email domains
const BLOCKED_EMAIL_DOMAINS = [
  'mailsec.com',
  'm3player.com',
  'tempmail.com',
  'temp-mail.org',
  'guerrillamail.com',
  'guerrillamail.org',
  'guerrillamail.net',
  'sharklasers.com',
  'grr.la',
  'guerrillamailblock.com',
  '10minutemail.com',
  '10minutemail.net',
  'minutemail.com',
  'tempail.com',
  'throwaway.email',
  'throwawaymail.com',
  'fakeinbox.com',
  'mailinator.com',
  'mailinator2.com',
  'mailinater.com',
  'mailinator.net',
  'trashmail.com',
  'trashmail.net',
  'trashmail.org',
  'mailnesia.com',
  'maildrop.cc',
  'getnada.com',
  'nada.email',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'cool.fr.nf',
  'jetable.fr.nf',
  'nospam.ze.tc',
  'nomail.xl.cx',
  'mega.zik.dj',
  'speed.1s.fr',
  'courriel.fr.nf',
  'moncourrier.fr.nf',
  'monemail.fr.nf',
  'monmail.fr.nf',
  'dispostable.com',
  'discard.email',
  'discardmail.com',
  'spamgourmet.com',
  'mytrashmail.com',
  'mailcatch.com',
  'mailscrap.com',
  'fakemailgenerator.com',
  'emailondeck.com',
  'tempr.email',
  'discardemail.com',
  'spamfree24.org',
  'spamfree24.de',
  'spamfree24.eu',
  'spamfree24.info',
  'spamfree24.net',
  'getairmail.com',
  'wegwerfmail.de',
  'wegwerfmail.net',
  'wegwerfmail.org',
  'mohmal.com',
  'tempinbox.com',
  'burnermail.io',
  'mailsac.com',
  'inboxkitten.com',
  'emailfake.com',
  'crazymailing.com',
  'tempemailco.com',
  'fakemail.net',
  'anonymbox.com',
];

const isBlockedEmailDomain = (email) => {
  const domain = email.split('@')[1]?.toLowerCase();
  return BLOCKED_EMAIL_DOMAINS.includes(domain);
};

// Detect phone code from OS timezone (e.g. Asia/Kolkata → IN → +91), fallback to +91
const getDefaultPhoneCode = () => {
  const countryCode = getCountryByTimezone();
  if (countryCode) {
    const country = getCountryByCode(countryCode);
    if (country) return country.phoneCode;
  }
  return '+91';
};

const DEFAULT_PHONE_CODE = getDefaultPhoneCode();

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, error, clearError } = useAuth();
  const { referralEnabled } = useSubscription();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phoneNumber: ''
  });
  const [selectedPhoneCode, setSelectedPhoneCode] = useState(DEFAULT_PHONE_CODE);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null); // null | { valid, referrerName }
  const [referralFromUrl, setReferralFromUrl] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [phoneCodeOpen, setPhoneCodeOpen] = useState(false);
  const [phoneCodeSearch, setPhoneCodeSearch] = useState('');
  const phoneCodeRef = useRef(null);
  const phoneCodeSearchRef = useRef(null);

  // Read ?ref= and UTM params from URL on mount
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setReferralFromUrl(true);
      // Also store in localStorage for Google OAuth flow
      localStorage.setItem('pendingReferralCode', ref.toUpperCase());
      // Validate the code
      referralAPI.validateCode(ref).then(res => {
        setReferralValid(res.data.data);
      }).catch(() => {
        setReferralValid({ valid: false });
      });
    }

    // Capture UTM params and store for registration
    const utmSource = searchParams.get('utm_source');
    if (utmSource) {
      const utmData = {
        source: utmSource,
        medium: searchParams.get('utm_medium') || undefined,
        campaign: searchParams.get('utm_campaign') || undefined,
        term: searchParams.get('utm_term') || undefined,
        content: searchParams.get('utm_content') || undefined,
      };
      localStorage.setItem('pendingUtm', JSON.stringify(utmData));
    }
  }, [searchParams]);

  // Close phone code dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (phoneCodeRef.current && !phoneCodeRef.current.contains(e.target)) {
        setPhoneCodeOpen(false);
        setPhoneCodeSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search when dropdown opens
  useEffect(() => {
    if (phoneCodeOpen && phoneCodeSearchRef.current) {
      phoneCodeSearchRef.current.focus();
    }
  }, [phoneCodeOpen]);

  const { registerField, focusNext } = useFormNavigation({
    onSubmit: () => document.getElementById('register-submit')?.click()
  });

  // Filtered phone code list
  const filteredPhoneCodes = useMemo(() => {
    if (!phoneCodeSearch) return PHONE_CODE_LIST;
    const q = phoneCodeSearch.toLowerCase();
    return PHONE_CODE_LIST.filter(c =>
      c.name.toLowerCase().includes(q) || c.phoneCode.includes(q)
    );
  }, [phoneCodeSearch]);

  // Selected country info from phone code
  const selectedCountry = useMemo(() => {
    return getCountryByPhoneCode(selectedPhoneCode);
  }, [selectedPhoneCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Password strength calculation
  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { score: 1, label: 'Weak', color: 'bg-red-500' };
    if (score <= 4) return { score: 2, label: 'Fair', color: 'bg-yellow-500' };
    return { score: 3, label: 'Strong', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const validate = () => {
    if (isBlockedEmailDomain(formData.email)) {
      setValidationError('Please use a valid email address. Temporary or disposable emails are not allowed.');
      return false;
    }
    // Password validation - must match server requirements
    const password = formData.password;
    const passwordErrors = [];
    if (password.length < 8) {
      passwordErrors.push('at least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      passwordErrors.push('one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      passwordErrors.push('one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      passwordErrors.push('one number');
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      passwordErrors.push('one special character');
    }
    if (passwordErrors.length > 0) {
      setValidationError(`Password must contain ${passwordErrors.join(', ')}`);
      return false;
    }
    // Phone number validation (digits only, reasonable length)
    const phoneDigits = formData.phoneNumber.replace(/\s/g, '');
    if (phoneDigits && !/^\d{6,14}$/.test(phoneDigits)) {
      setValidationError('Please enter a valid phone number (digits only)');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    // Build E.164 phone number
    const phoneDigits = formData.phoneNumber.replace(/\s/g, '');
    const phone = phoneDigits ? `${selectedPhoneCode}${phoneDigits}` : '';

    // Derive country from selected phone code
    const country = selectedCountry?.code || '';

    // Get stored UTM data
    const storedUtm = localStorage.getItem('pendingUtm');
    const utm = storedUtm ? JSON.parse(storedUtm) : undefined;

    try {
      await register({
        email: formData.email,
        password: formData.password,
        phone,
        country,
        referralCode: referralCode || undefined,
        utm
      });
      // Clear stored referral code and UTM
      localStorage.removeItem('pendingReferralCode');
      localStorage.removeItem('pendingUtm');
      navigate('/');
    } catch {
      // Error is set by AuthContext and displayed via Alert
      // Stay on page - don't navigate
    } finally {
      setLoading(false);
    }
  };

  const displayError = validationError || error;

  return (
    <div className="min-h-[100dvh] bg-surface-50 flex">
      {/* Left Panel - Branding */}
      <div
        className="hidden xl:flex lg:w-1/2 relative overflow-hidden"
        style={{ backgroundColor: 'var(--sidebar-bg)' }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute top-40 left-10 w-80 h-80 rounded-full blur-3xl"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-income-500 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          {/* Logo */}
          <div className="mb-8">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-14 h-14 mb-6 rounded-full"
            />
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Start your<br />financial journey
            </h1>
            <p className="text-surface-400 text-lg mt-4 max-w-md">
              Take control of your money. Track, analyze, and grow your wealth.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4 mt-8">
            {[
              'Track income & expenses easily',
              'Secure & encrypted data',
              'Multi-currency & multi-account',
              'Smart reports & budget alerts'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-surface-300">
                <div className="w-5 h-5 rounded-full bg-income-500/20 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-income-500" />
                </div>
                {feature}
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Right Panel - Register Form */}
      <KeyboardDismissView className="flex-1 flex items-start xl:items-center justify-center p-6 pt-10 sm:p-12 overflow-y-auto safe-area-top">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="xl:hidden text-center mb-6">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-14 h-14 mx-auto mb-3 rounded-2xl"
            />
          </div>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-surface-900">
              Create account
            </h2>
            <p className="text-surface-500 mt-1.5">
              Your finances. Private. Secure.
            </p>
          </div>

          {/* Social Sign-In */}
          <div className="space-y-3 mb-6">
            <GoogleLoginButton
              onSuccess={() => navigate('/')}
              onError={(err) => setValidationError(err)}
              text="signup_with"
            />
            <AppleLoginButton
              onSuccess={() => navigate('/')}
              onError={(err) => setValidationError(err)}
              text="sign_up"
            />
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-surface-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-surface-50 text-surface-400">or continue with email</span>
            </div>
          </div>

          {displayError && (
            <Alert
              variant="error"
              message={displayError}
              onClose={() => {
                clearError();
                setValidationError('');
              }}
            />
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                <input
                  ref={(el) => registerField('email', el)}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  enterKeyHint="next"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), focusNext('email'))}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-surface-300 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>

            {/* Phone — country code dropdown + number input */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Phone number <span className="text-surface-400 font-normal">(Optional)</span>
              </label>
              <div className="flex gap-1.5">
                {/* Phone code dropdown */}
                <div className="relative shrink-0" ref={phoneCodeRef}>
                  <button
                    type="button"
                    onClick={() => setPhoneCodeOpen(!phoneCodeOpen)}
                    className="flex items-center gap-0.5 px-2.5 py-3 bg-white border border-surface-300 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all whitespace-nowrap"
                  >
                    <span className="text-sm font-medium">{selectedPhoneCode}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-surface-400" />
                  </button>
                  {phoneCodeOpen && (
                    <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-surface-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
                      <div className="p-2 border-b border-surface-100">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                          <input
                            ref={phoneCodeSearchRef}
                            type="text"
                            value={phoneCodeSearch}
                            onChange={(e) => setPhoneCodeSearch(e.target.value)}
                            placeholder="Search country..."
                            className="w-full pl-9 pr-3 py-2 text-sm bg-surface-50 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-surface-900"
                          />
                        </div>
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {filteredPhoneCodes.map((c) => (
                          <button
                            key={c.code}
                            type="button"
                            onClick={() => {
                              setSelectedPhoneCode(c.phoneCode);
                              setPhoneCodeOpen(false);
                              setPhoneCodeSearch('');
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-50 transition-colors flex items-center justify-between ${
                              selectedPhoneCode === c.phoneCode ? 'bg-surface-50 font-medium' : ''
                            }`}
                          >
                            <span className="truncate">{c.name}</span>
                            <span className="text-surface-500 ml-2 shrink-0">{c.phoneCode}</span>
                          </button>
                        ))}
                        {filteredPhoneCodes.length === 0 && (
                          <p className="px-3 py-2 text-sm text-surface-400">No countries found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* Phone number input */}
                <div className="relative flex-1">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                  <input
                    ref={(el) => registerField('phoneNumber', el)}
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) => {
                      // Allow only digits
                      const val = e.target.value.replace(/[^\d]/g, '');
                      setFormData({ ...formData, phoneNumber: val });
                    }}
                    placeholder="9876543210"
                    enterKeyHint="done"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), focusNext('phoneNumber'))}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-surface-300 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                <input
                  ref={(el) => registerField('password', el)}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  enterKeyHint="next"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), focusNext('password'))}
                  className="w-full pl-12 pr-12 py-3 bg-white border border-surface-300 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex gap-1.5">
                    {[1, 2, 3].map((level) => (
                      <div
                        key={level}
                        className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                          level <= passwordStrength.score ? passwordStrength.color : 'bg-surface-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs mt-1 ${
                    passwordStrength.score === 1 ? 'text-red-500' :
                    passwordStrength.score === 2 ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {passwordStrength.label}
                  </p>
                </div>
              )}
            </div>

            {/* Referral Code - hidden on iOS (Apple 3.1.1) and when referral program is disabled */}
            {!isIOS && referralEnabled && (
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Referral Code (optional)
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Gift className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => {
                      if (!referralFromUrl) {
                        setReferralCode(e.target.value.toUpperCase());
                        setReferralValid(null);
                      }
                    }}
                    placeholder="Enter referral code"
                    readOnly={referralFromUrl}
                    className={`w-full pl-12 pr-4 py-3 bg-white border rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all ${
                      referralFromUrl ? 'border-green-300 bg-green-50' : 'border-surface-200'
                    }`}
                  />
                </div>
                {!referralFromUrl && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!referralCode.trim()) return;
                      referralAPI.validateCode(referralCode).then(res => {
                        setReferralValid(res.data.data);
                      }).catch(() => {
                        setReferralValid({ valid: false });
                      });
                    }}
                    disabled={!referralCode.trim()}
                    className="px-4 py-3 rounded-xl text-sm font-medium border border-surface-200 bg-surface-50 hover:bg-surface-100 text-surface-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Validate
                  </button>
                )}
              </div>
              {referralValid?.valid && (
                <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Valid</span>
                  {referralValid.referrerName && <span>Referred by {referralValid.referrerName}</span>}
                </p>
              )}
              {referralValid && !referralValid.valid && (
                <p className="text-xs text-red-500 mt-1.5">Invalid referral code</p>
              )}
            </div>
            )}

            {/* Terms & Privacy Consent */}
            <div className="flex items-start gap-3 mt-2">
              <button
                type="button"
                onClick={() => setAgreedToTerms(!agreedToTerms)}
                className={`mt-0.5 w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-all duration-200 ${
                  agreedToTerms
                    ? 'border-transparent bg-[var(--accent-bg)] text-white'
                    : 'border-surface-300 bg-white hover:border-surface-400'
                }`}
                aria-checked={agreedToTerms}
                role="checkbox"
              >
                {agreedToTerms && (
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <span className="text-sm text-surface-500 leading-snug cursor-pointer select-none" onClick={(e) => { if (e.target.tagName !== 'BUTTON') setAgreedToTerms(!agreedToTerms); }}>
                I agree to the{' '}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openExternalUrl('https://hisabjourney.com/terms'); }}
                  className="font-medium underline"
                  style={{ color: 'var(--accent-bg)' }}
                >
                  Terms of Service
                </button>{' '}
                and{' '}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openExternalUrl('https://hisabjourney.com/privacy'); }}
                  className="font-medium underline"
                  style={{ color: 'var(--accent-bg)' }}
                >
                  Privacy Policy
                </button>
              </span>
            </div>

            {/* Submit */}
            <button
              id="register-submit"
              type="submit"
              className={`w-full py-3.5 rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 group mt-4 ${
                loading ? 'opacity-60 cursor-not-allowed' : !agreedToTerms ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-lg hover:-translate-y-px active:scale-[0.98]'
              }`}
              style={{
                backgroundColor: 'var(--accent-bg)',
                color: 'var(--accent-text)',
                boxShadow: agreedToTerms && !loading ? '0 4px 14px -3px rgba(59, 130, 246, 0.4)' : undefined
              }}
              disabled={loading || !agreedToTerms}
            >
              {loading ? (
                'Creating account...'
              ) : (
                <>
                  Create account
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-surface-500 mt-8">
            Already have an account?{' '}
            <Link
              to="/login"
              className="hover:underline font-medium"
              style={{ color: 'var(--accent-bg)' }}
            >
              Sign in
            </Link>
          </p>

        </div>
      </KeyboardDismissView>
    </div>
  );
}
